import React, { useRef, useState, useEffect, useContext } from "react";
import Navtabs from "./NavTabs";
import SubNav from "./SubNav";
import { canSeeSalesSubTab, defaultSalesSubMenu } from "./salesSubTabs";
import { emitter } from "./UtilityFunctions";
import { GlobalContext } from "./GlobalContext";
import useDiscardGuard from "./useDiscardGuard";
import useSheetDensity from "./hooks/useSheetDensity";

//The sub-menu a tab lands on when nothing more specific is asked for. Was
//written out three times with three different answers; this is the one that
//actually ran (onTabClick's), so it is the one kept.
//
//Sales is the only one that has to ask: it lands on Credit, and the two credit
//sheets need credit.view, which the default Inventory Manager role does not
//hold. That role's first click on Sales opened an empty sheet and an "Access
//denied: requires credit.view permission" toast. defaultSalesSubMenu keeps
//Credit for everyone who can read it and picks the first readable sheet for
//everyone else.
function defaultSubMenu(tabId, can) {
    switch (tabId) {
        case "tab-inventory": return "Inventory";
        case "tab-sales":     return defaultSalesSubMenu(can);
        case "tab-logistics": return "NewPurchase";
        case "tab-customers": return "Customers";
        case "tab-brokers":   return "Brokers";
        case "tab-messages":  return "History";
        default:              return "";
    }
}

function CarsNav({ logInStatus, setLogInStatus, tourRunning }){
    const { hasUnsavedChanges, can } = useContext(GlobalContext);
    //Mounted here (not in SheetFooter) so <html data-sheet-density> is set for
    //every sheet, including the ones without a footer (Inventory/Committed/Credit).
    useSheetDensity();
    //The modal tables' scope is mounted here too, for a subtler reason: the
    //attribute is written in an effect, so a tab that mounted its own would
    //paint one frame at the 32px default before jumping to a saved "roomy".
    //Setting it up front means the CSS is already right when the tab appears.
    useSheetDensity("grid");
    const confirmLeave = useDiscardGuard();
    //Keep latest guard fns in refs so the mount-once emitter handler stays
    //fresh. `can` rides along for the same reason: the handler is registered
    //once, and a permission read from that stale closure would be the session's
    //first render forever.
    const guardRef = useRef({ hasUnsavedChanges, confirmLeave, can });
    guardRef.current = { hasUnsavedChanges, confirmLeave, can };
    const [ activeTab, setActiveTab ] = useState("tab-dashboard");
    const [ disableTabsForTour, setDisableTabsForTour ] = useState(false);
    const [activeSubMenu, setActiveSubMenu] = useState(()=>defaultSubMenu(activeTab, can));
    const switchActiveSubMenu = (newSubMenu)=>{
        setActiveSubMenu(newSubMenu);
    }

    useEffect(() => {
        const handler = async ({ tab, subMenu }) => {
            const { hasUnsavedChanges, confirmLeave, can } = guardRef.current;
            if (!(await confirmLeave(hasUnsavedChanges()))) return;
            setActiveTab(tab);
            //A caller that names only a tab gets that tab's default sub-menu.
            //It used to get undefined, which renders nothing at all — most
            //sections are `activeSubMenu === "X" && <Section/>`.
            //A caller that names one the user cannot open (clearing a credit
            //deal jumps to CreditCleared, and credit.clear does not imply
            //credit.view) gets the default instead of a sheet that renders
            //nothing.
            setActiveSubMenu(subMenu && canSeeSalesSubTab(subMenu, can)
                ? subMenu : defaultSubMenu(tab, can));
        };
        emitter.on("navigate", handler);
        return () => emitter.removeListener("navigate", handler);
    }, []);

    //Called with a plain "tab-…" id string from every entry point (desktop
    //strip, More menus, mobile bottom nav) so all share the guard + submenu
    //logic. Never pass the click event: with icon/span children inside the
    //buttons, event.target.id would be empty.
    const onTabClick = async (tabId)=>{
        //Warn before leaving a view that has unsaved edits (e.g. Settings).
        if (!(await confirmLeave(hasUnsavedChanges()))) return;
        setActiveTab(tabId);
        setActiveSubMenu(defaultSubMenu(tabId, can));
    }

    return (
        <>
            <div className="carsnav">

                <Navtabs
                    activeTab={activeTab}
                    onTabClick={onTabClick}
                    disableTabsForTour={disableTabsForTour}
                    tourRunning={tourRunning}
                />
                <SubNav activeTab={activeTab}  
                    activeSubMenu={activeSubMenu} 
                    switchActiveSubMenu={switchActiveSubMenu}
                    setLogInStatus={setLogInStatus} 
                    logInStatus={logInStatus}
                    disableTabsForTour={disableTabsForTour}
                    setDisableTabsForTour={setDisableTabsForTour}
                />
            </div>
        </>
    )
}

export default CarsNav;